/**
 * ValveCrank — Basil's Turn-Table Touch target in the Overgrown
 * Conservatory. Each crank represents a "handle" on the Master Mist
 * Regulator or a secondary regulator, and requests a canonical
 * Conservatory cycle flip when Basil lands enough TTT rhythm hits.
 *
 * Per `docs/specs/SPEC-conservatory-rebuild.md` §2.2, §4 and §5.1:
 *   - Primary regulator lives at `(22, 1.3, -2.1)` with two variants
 *     (incubation + irrigation), choked by a petrified vine until the
 *     cats' Resonant Drop shatters it (MasterValvePuzzle).
 *   - The escort's mist valve (irrigation variant) lives at
 *     `(114.5, 1.3, -2.1)`: Basil re-mists the deep warren each time the
 *     escort's Irrigation wears thin (NectarEscortPuzzle).
 *   - Three more cranks live on the Sundew base at `(131, 4)`,
 *     `(135, 4)`, `(133, 7)` for the Phase 1 apex fight — those
 *     cranks use `requiredHits=3` with a short activation window and
 *     emit `sundewValve:activated` on completion instead of a
 *     cycle flip.
 *
 * Spec also calls for Minna's Purr pre-charging a crank — a Purred
 * valve completes in 2 hits instead of 3. This class exposes a
 * `prime()` method the Purr ability calls on proximity to mark the
 * crank as primed for the next interaction window.
 */
import { Scene } from "@babylonjs/core/scene";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Animation } from "@babylonjs/core/Animations/animation";
// Side-effect import: registers Scene.prototype.beginAnimation (tree-shaken
// out otherwise — Engine.ts imports it for the game, but headless consumers
// like the sundew-apex integration spec construct bare NullEngine scenes).
import "@babylonjs/core/Animations/animatable";
import { InteractiveObject, InteractiveConfig } from "./InteractiveObject";
import { gameEvents } from "../../core/EventBus";
import { GameEvents } from "../../config/events";
import { COLORS } from "../../config/GameConfig";
import type { ConservatoryPhase } from "../../systems/ConservatoryCycle";

/** Which cycle phase this crank flips to when activated. */
export type ValveCrankVariant = "incubation" | "irrigation" | "sundew";

export interface ValveCrankConfig extends Omit<InteractiveConfig, "size"> {
  size?: Vector3;
  variant: ValveCrankVariant;
  /** Hits required to activate. Default 3. */
  requiredHits?: number;
  /**
   * Hits required when the crank is primed by Minna's Purr. Default 2.
   * Ignored for sundew valves (no Purr priming on the apex).
   */
  primedHits?: number;
}

const VARIANT_COLORS: Record<ValveCrankVariant, { body: Color3; accent: Color3; idle: Color3 }> = {
  incubation: {
    body: Color3.FromHexString("#8b3a1f"),
    accent: Color3.FromHexString("#d66b2a"),
    idle: new Color3(0.08, 0.02, 0.01),
  },
  irrigation: {
    body: Color3.FromHexString("#2f5f6c"),
    accent: Color3.FromHexString("#5bc0d5"),
    idle: new Color3(0.01, 0.04, 0.06),
  },
  sundew: {
    body: Color3.FromHexString("#3a4a1f"),
    accent: Color3.FromHexString("#8cc44a"),
    idle: new Color3(0.02, 0.05, 0.01),
  },
};

export class ValveCrank extends InteractiveObject {
  private readonly variant: ValveCrankVariant;
  private readonly requiredHits: number;
  private readonly primedHits: number;
  private currentHits = 0;
  private primed = false;
  private activated = false;

  private cranksMat!: StandardMaterial;
  private accentMat!: StandardMaterial;
  private wheelMesh: Mesh | null = null;
  private accentMeshes: Mesh[] = [];

  /**
   * Q-032: the hand-wheel + spokes hang off this spinner so each TTT hit
   * can visibly crank them. The wheel already faces the +Z camera
   * (rotation.x = π/2 on the torus), so spinning the parent's rotation.z
   * turns it in the camera plane. One full revolution per completed crank.
   */
  private wheelSpinner: TransformNode | null = null;
  /** Accumulated animation target angle (radians about Z, negative = clockwise). */
  private wheelTargetAngle = 0;

  constructor(scene: Scene, config: ValveCrankConfig) {
    const size = config.size ?? new Vector3(0.9, 1.4, 0.9);
    super(scene, { ...config, size });

    this.variant = config.variant;
    this.requiredHits = config.requiredHits ?? 3;
    this.primedHits = config.primedHits ?? 2;

    this.buildVisual(size);
  }

  /**
   * Minna's Purr primes this crank so Basil needs one fewer hit.
   * Sundew valves ignore this — the apex fight requires full hits.
   */
  prime(): void {
    if (this.variant === "sundew") return;
    if (this.activated) return;
    if (this.primed) return;
    this.primed = true;
    this.refreshMaterials();
  }

  /** Reset to unprimed state after the crank completes or a cycle change. */
  unprime(): void {
    if (!this.primed) return;
    this.primed = false;
    this.refreshMaterials();
  }

  /**
   * Called by the TTT ability pipeline on every successful hit while
   * Basil stands within interaction range. Returns `true` if the hit
   * counted (i.e. the crank isn't already activated), `false` otherwise.
   *
   * When the hit count reaches the (primed-adjusted) threshold the
   * crank emits `CONSERVATORY_CYCLE_REQUESTED` for regulator variants
   * or `sundewValve:activated` for the apex variant.
   */
  registerHit(): boolean {
    if (this.activated) return false;
    this.currentHits += 1;
    this.refreshMaterials();

    const needed = this.primed ? this.primedHits : this.requiredHits;

    // Q-032: crank the hand-wheel proportional to hits — one full
    // clockwise (from the camera) revolution per completed crank, so a
    // primed 2-hit crank turns π per hit and a 3-hit crank turns 2π/3.
    this.advanceWheel(-(Math.PI * 2) / Math.max(1, needed), 6);

    // Forensic-QA event: per-hit milestone on sundew apex valves so
    // telemetry can see the ramp, not just the activation. Kept scoped
    // to the sundew variant because the cycle regulator variants already
    // produce a CONSERVATORY_CYCLE_REQUESTED on completion and per-hit
    // tracking on them would flood the recorder with noise.
    if (this.variant === "sundew") {
      gameEvents.emit(GameEvents.SUNDEW_VALVE_HIT, {
        key: this.key,
        currentHits: this.currentHits,
        required: needed,
        primed: this.primed,
      });
    }

    if (this.currentHits >= needed) {
      this.completeActivation();
    }
    return true;
  }

  onInteract(abilityId: string): boolean {
    if (abilityId === "turnTableTouch") {
      return this.registerHit();
    }
    if (abilityId === "calmingResonance") {
      this.prime();
      return true;
    }
    return false;
  }

  override getHighlightTarget(): AbstractMesh {
    return this.wheelMesh ?? this.rootMesh;
  }

  /** How many more hits Basil needs. */
  get hitsRemaining(): number {
    if (this.activated) return 0;
    const needed = this.primed ? this.primedHits : this.requiredHits;
    return Math.max(0, needed - this.currentHits);
  }

  get isActivated(): boolean {
    return this.activated;
  }

  get isPrimed(): boolean {
    return this.primed;
  }

  get valveVariant(): ValveCrankVariant {
    return this.variant;
  }

  /** Reset to idle after a cycle flip lands. */
  resetCycle(): void {
    this.currentHits = 0;
    this.activated = false;
    this.primed = false;
    this.state = "idle";
    this.refreshMaterials();
    // Intentionally NOT resetting the wheel angle — a hand-wheel's
    // absolute angle carries no gameplay meaning, and snapping it back
    // would read as the crank "undoing" the player's work.
  }

  /**
   * Test/QA introspection: the accumulated wheel-spin target angle
   * (radians about Z; −2π per completed crank).
   */
  get wheelSpinAngleTarget(): number {
    return this.wheelTargetAngle;
  }

  /** Test/QA introspection: the spinner node the wheel + spokes hang off. */
  get wheelSpinnerNode(): TransformNode | null {
    return this.wheelSpinner;
  }

  // ─── Private ──────────────────────────────────────────────────

  private completeActivation(): void {
    this.activated = true;
    this.state = "active";

    // Celebratory full spin on completion. This beginAnimation supersedes
    // the per-hit step started moments ago in registerHit; the target
    // angle accumulates both, so the wheel lands on step + victory lap.
    this.advanceWheel(-Math.PI * 2, 15);

    this.refreshMaterials();

    if (this.variant === "sundew") {
      gameEvents.emit(GameEvents.SUNDEW_VALVE_ACTIVATED, {
        key: this.key,
      });
      return;
    }

    const targetPhase: ConservatoryPhase = this.variant;
    gameEvents.emit(GameEvents.CONSERVATORY_CYCLE_REQUESTED, {
      target: targetPhase,
      accepted: true,
      sourceKey: this.key,
    });
  }

  private buildVisual(size: Vector3): void {
    const palette = VARIANT_COLORS[this.variant];

    this.cranksMat = new StandardMaterial(
      `valveCrank_${this.key}_body`,
      this.scene,
    );
    this.cranksMat.diffuseColor = palette.body;
    this.cranksMat.specularColor = Color3.FromHexString(COLORS.BRASS).scale(
      0.3,
    );
    this.cranksMat.emissiveColor = palette.idle;

    this.accentMat = new StandardMaterial(
      `valveCrank_${this.key}_accent`,
      this.scene,
    );
    this.accentMat.diffuseColor = palette.accent;
    this.accentMat.specularColor = Color3.Black();
    this.accentMat.emissiveColor = palette.idle.scale(1.5);

    // Brass pedestal — the valve housing.
    const pedestal = MeshBuilder.CreateCylinder(
      `valveCrank_${this.key}_pedestal`,
      { diameter: size.x, height: size.y * 0.6, tessellation: 14 },
      this.scene,
    );
    pedestal.parent = this.rootMesh;
    pedestal.position = new Vector3(0, size.y * 0.3, 0);
    pedestal.material = this.cranksMat;
    this.accentMeshes.push(pedestal);

    // Spinner node — wheel + spokes parent here so registerHit can crank
    // them about the camera-facing Z axis (Q-032; see field docs).
    const spinner = new TransformNode(
      `valveCrank_${this.key}_spinner`,
      this.scene,
    );
    spinner.parent = this.rootMesh;
    spinner.position = new Vector3(0, size.y * 0.85, 0);
    this.wheelSpinner = spinner;

    // Wheel — the rotating handle itself.
    const wheel = MeshBuilder.CreateTorus(
      `valveCrank_${this.key}_wheel`,
      { diameter: size.x * 0.95, thickness: 0.08, tessellation: 18 },
      this.scene,
    );
    wheel.parent = spinner;
    wheel.position = Vector3.Zero();
    wheel.rotation.x = Math.PI / 2; // already faces the +Z camera
    wheel.material = this.accentMat;
    this.wheelMesh = wheel;

    // Three spokes so the wheel reads as a proper hand-wheel.
    for (const [index, rot] of [0, Math.PI / 3, -Math.PI / 3].entries()) {
      const spoke = MeshBuilder.CreateCylinder(
        `valveCrank_${this.key}_spoke_${index}`,
        { height: size.x * 0.78, diameter: 0.05, tessellation: 8 },
        this.scene,
      );
      spoke.parent = spinner;
      spoke.position = Vector3.Zero();
      spoke.rotation.z = rot;
      spoke.material = this.accentMat;
      this.accentMeshes.push(spoke);
    }

    this.material = this.cranksMat;
  }

  /**
   * Advance the wheel-spin target by `deltaRad` and animate the spinner
   * from its current angle to the new target over `frames` (30fps) —
   * the same cheap 6-frame Animation pattern CraneLever runs in L1.
   * beginAnimation restarts any in-flight spin on the node, and the
   * onAnimationEnd pins the exact target so interrupted spins can't
   * accumulate drift.
   */
  private advanceWheel(deltaRad: number, frames: number): void {
    this.wheelTargetAngle += deltaRad;
    const spinner = this.wheelSpinner;
    if (!spinner) return;

    const fps = 30;
    const target = this.wheelTargetAngle;
    const spinAnim = new Animation(
      `valveCrankSpin_${this.key}_${Date.now()}`,
      "rotation.z",
      fps,
      Animation.ANIMATIONTYPE_FLOAT,
      Animation.ANIMATIONLOOPMODE_CONSTANT,
    );
    spinAnim.setKeys([
      { frame: 0, value: spinner.rotation.z },
      { frame: frames, value: target },
    ]);

    spinner.animations = [spinAnim];
    this.scene.beginAnimation(spinner, 0, frames, false, 1, () => {
      spinner.rotation.z = target;
    });
  }

  private refreshMaterials(): void {
    if (!this.accentMat || !this.cranksMat) return;
    const palette = VARIANT_COLORS[this.variant];

    if (this.activated) {
      // Solved: pulse the accent bright.
      this.cranksMat.emissiveColor = palette.idle.scale(2);
      this.accentMat.emissiveColor = palette.accent.scale(0.6);
      return;
    }

    if (this.primed) {
      // Minna's Purr primed the crank — glow shifts green-ish regardless
      // of variant so the visual tell is consistent across all cranks.
      this.cranksMat.emissiveColor = new Color3(0.04, 0.1, 0.02);
      this.accentMat.emissiveColor = Color3.FromHexString(
        COLORS.BIOLUMINESCENT_GREEN,
      ).scale(0.35);
      return;
    }

    const progressRatio = this.currentHits / this.requiredHits;
    this.cranksMat.emissiveColor = palette.idle.scale(1 + progressRatio);
    this.accentMat.emissiveColor = palette.accent.scale(
      0.1 + progressRatio * 0.35,
    );
  }
}
